import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import AuthModal from "./AuthModal";
import FormInput from "../../../common/FormInput";
import SocialLogin from "./SocialLogin";
import { loginUser, sendOtp, verifyOtp, resetPassword, clearError, clearForgotPasswordState } from "../../../../redux/slices/authSlice";
import { syncUserFromAuth } from "../../../../redux/slices/profileSlice";
import MessageBox from "../../../common/MessageBox";
import { callAPI } from "../../../../utils/apiUtils";
import { apiUrls } from "../../../../utils/apiUrls";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, forgotPassword: forgotState, otpLoading, otpError, otpVerified, registeredUserId } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({ email: "", password: "", });
  const [validationErrors, setValidationErrors] = useState({ email: "", password: "", });
  const [submitted, setSubmitted] = useState(false);
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotErrors, setForgotErrors] = useState({});
  const [localLoading, setLocalLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [messageBox, setMessageBox] = useState({ show: false, type: 'success', text: '' });

  const showMessage = (type, text, duration = 3000) => {
    setMessageBox({ show: true, type, text });
    setTimeout(() => {
      setMessageBox(prev => ({ ...prev, show: false }));
    }, duration);
  };

  const loginConfig = {
    title: "Hi, Welcome Back!",
    subtitle: "Sign in to access your account and opportunities.",
    buttonText: "Sign in",
    footerText: "Don't have an account?",
    footerLink: "/signup",
    footerLinkText: "Sign Up"
  };

  const forgotConfig = {
    title: "Forgot Password",
    step1Subtitle: "Enter your email address and we'll send you an OTP to reset your password.",
    step2Subtitle: "Enter the 6-digit OTP sent to your email.",
    step3Subtitle: "Enter your new password.",
    buttonText: "Send OTP",
    verifyText: "Verify OTP",
    resetText: "Reset Password"
  };

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            setCanResend(true);
            clearInterval(interval);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const startTimer = () => {
    setTimer(30);
    setCanResend(false);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return "Email is required";
    } else if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePassword = (password) => {
    if (!password) {
      return "Password is required";
    } else if (password.length < 6) {
      return "Password must be at least 6 characters";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (submitted && validationErrors[name]) {
      setValidationErrors({
        ...validationErrors, [name]: ""
      });
    }
    if (error) dispatch(clearError());
  };

  const validateForm = () => {
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    setValidationErrors({ email: emailError, password: passwordError });
    return !emailError && !passwordError;
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSubmitted(true);
    if (!validateForm()) return;
    const resultAction = await dispatch(loginUser(formData));
    if (loginUser.fulfilled.match(resultAction)) {
      if (resultAction.payload?.user) {
        dispatch(syncUserFromAuth(resultAction.payload.user));
      }
      showMessage('success', resultAction?.payload?.message || "Login successful");
      setTimeout(() => {
        const closeButton = document.querySelector("[data-bs-dismiss='modal']");
        if (closeButton) {
          closeButton.click();
        }
        const role = resultAction.payload.user.role;
        const dashboardPath = role === 'SHIPPER' ? '/carrer-dashboard' : '/user-dashboard';
        const onboardingPath = role === 'SHIPPER' ? '/company-info' : '/transactions';
        callAPI(apiUrls.walletGet, {}, 'GET', {})
          .then((res) => {
            const isOnboarded = !!res?.data?.data?.stripeOnboardingComplete;
            navigate(isOnboarded ? dashboardPath : onboardingPath);
          })
          .catch(() => {
            navigate(dashboardPath);
          });
      }, 1500);
    } else {
      if (resultAction.payload) {
        showMessage('error', resultAction.payload.message || "Login failed");
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSocialLogin = (provider) => {
    console.log(`Login with ${provider}`);
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        const nextInput = document.getElementById(`fp-otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const validateForgotEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!forgotEmail) {
      setForgotErrors({ email: 'Email is required' });
      return false;
    }
    if (!emailRegex.test(forgotEmail)) {
      setForgotErrors({ email: 'Please enter a valid email address' });
      return false;
    }
    return true;
  };

  const validateOtp = () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setForgotErrors({ otp: 'Please enter complete 6-digit OTP' });
      return false;
    }
    return true;
  };

  const validateNewPassword = () => {
    const errors = {};
    if (!newPassword) {
      errors.password = 'Password is required';
    } else if (newPassword.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setForgotErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!validateForgotEmail()) return;
    setLocalLoading(true);
    const result = await dispatch(sendOtp(forgotEmail));
    if (sendOtp.fulfilled.match(result)) {
      showMessage('success', 'OTP sent successfully to your email');
      setStep(2);
      startTimer();
    } else {
      if (result.payload) {
        console.log("result ", result.payload)
        showMessage('error', result.payload || 'Failed to send OTP');
      }
    }
    setLocalLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!validateOtp()) return;
    setLocalLoading(true);
    const otpString = otp.join('');
    const userId = registeredUserId || forgotState?.userId;
    if (!userId) {
      showMessage('error', 'User ID not found. Please try again.');
      setLocalLoading(false);
      return;
    }
    const result = await dispatch(verifyOtp({ userId: userId, otp: otpString }));
    if (verifyOtp.fulfilled.match(result)) {
      showMessage('success', 'OTP verified successfully');
      setStep(3);
    } else {  
      const errorMessage = String(result.payload || 'Invalid OTP');
      showMessage('error', errorMessage);
    }
    setLocalLoading(false);
  };

  const handleResetPassword = async () => {
    if (!validateNewPassword()) return;
    setLocalLoading(true);
    const userId = registeredUserId || forgotState?.userId;
    if (!userId) {
      showMessage('error', 'User ID not found. Please try again.');
      setLocalLoading(false);
      return;
    }
    const result = await dispatch(resetPassword({ _id: userId, password: newPassword, confirmPassword }));
    if (resetPassword.fulfilled.match(result)) {
      showMessage('success', 'Password reset successfully! Please login with new password');
      setTimeout(() => {
        handleBackToLogin();
      }, 1500);
    } else {
      if (result.payload) {
        showMessage('error', result.payload.message || 'Failed to reset password');
      }
    }
    setLocalLoading(false);
  };

  const handleResendOtp = async () => {
    if (!canResend) {
      showMessage('error', `Please wait ${timer} seconds before requesting another OTP`);
      return;
    }
    setLocalLoading(true);
    const result = await dispatch(sendOtp(forgotEmail));
    if (sendOtp.fulfilled.match(result)) {
      showMessage('success', 'OTP resent successfully');
      startTimer();
    } else {
      showMessage('error', result.payload || 'Failed to resend OTP');
    }
    setLocalLoading(false);
  };

  const handleBackToLogin = () => {
    setIsForgotMode(false);
    setStep(1);
    setForgotEmail('');
    setOtp(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
    setForgotErrors({});
    setTimer(0);
    setCanResend(true);
    dispatch(clearForgotPasswordState());
  };

  const isLoading = loading || otpLoading || localLoading;

  return (
    <AuthModal id="loginModal" onClose={() => {
      dispatch(clearError());
      if (isForgotMode) handleBackToLogin();
    }}>
      <div className="login-fileds">
        {!isForgotMode ? (
          <>
            <div className="login-header mb-4 text-center">
              <h5 className="mb-2">{loginConfig.title}</h5>
              <p>{loginConfig.subtitle}</p>
            </div>
            {messageBox.show && (
              <div >
                <MessageBox
                  type={messageBox.type}
                  message={messageBox.text}
                  onClose={() => setMessageBox(prev => ({ ...prev, show: false }))}
                  showIcon={true}
                  showCloseButton={true}
                />
              </div>
            )}
            <FormInput type="email" name="email" placeholder="Enter your email address" value={formData.email} onChange={handleChange} onKeyDown={handleKeyPress} icon="/image/profile.svg" error={error} validationError={submitted ? validationErrors.email : ""} />
            <FormInput name="password" placeholder="Password" value={formData.password} onChange={handleChange} onKeyDown={handleKeyPress} icon="/image/lock.svg" error={error} isPassword={true} validationError={submitted ? validationErrors.password : ""} />
            <div className="text-end mb-3">
              <button type="button" className="border-0 bg-transparent text-danger text-decoration-none" style={{ fontSize: '14px', cursor: 'pointer' }} onClick={() => setIsForgotMode(true)}>
                Forgot Password?
              </button>
            </div>
            <div className="form-group mb-3 position-relative">
              <button type="button" className="btn red-btn w-100" onClick={handleSubmit} disabled={isLoading} style={{ opacity: isLoading ? 0.7 : 1 }}>
                {isLoading ? "Signing in..." : loginConfig.buttonText}
                {!isLoading && (
                  <span>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.92 11.62C17.8724 11.4973 17.801 11.3851 17.71 11.29L12.71 6.29C12.6168 6.19676 12.5061 6.1228 12.3842 6.07234C12.2624 6.02188 12.1319 5.99591 12 5.99591C11.7337 5.99591 11.4783 6.1017 11.29 6.29C11.1968 6.38324 11.1228 6.49393 11.0723 6.61575C11.0219 6.73758 10.9959 6.86814 10.9959 7C10.9959 7.2663 11.1017 7.5217 11.29 7.71L14.59 11H7C6.73478 11 6.48043 11.1054 6.29289 11.2929C6.10536 11.4804 6 11.7348 6 12C6 12.2652 6.10536 12.5196 6.29289 12.7071C6.48043 12.8946 6.73478 13 7 13H14.59L11.29 16.29C11.1963 16.383 11.1219 16.4936 11.0711 16.6154C11.0203 16.7373 10.9942 16.868 10.9942 17C10.9942 17.132 11.0203 17.2627 11.0711 17.3846C11.1219 17.5064 11.1963 17.617 11.29 17.71C11.383 17.8037 11.4936 17.8781 11.6154 17.9289C11.7373 17.9797 11.868 18.0058 12 18.0058C12.132 18.0058 12.2627 17.9797 12.3846 17.9289C12.5064 17.8781 12.617 17.8037 12.71 17.71L17.71 12.71C17.801 12.6149 17.8724 12.5028 17.92 12.38C18.02 12.1365 18.02 11.8635 17.92 11.62Z" fill="white" />
                    </svg>
                  </span>
                )}
              </button>
            </div>
            <SocialLogin onSocialLogin={handleSocialLogin} />
            <p className="text-center mb-0 auth-bottom" data-bs-dismiss="modal">
              {loginConfig.footerText}{" "}
              <Link to={loginConfig.footerLink} className="link-text">{loginConfig.footerLinkText}</Link>
            </p>
          </>
        ) : (
          <>
            <div className="login-header mb-4 text-center">

              <h5 className="mb-2">{forgotConfig.title}</h5>
              <p>
                {step === 1 && forgotConfig.step1Subtitle}
                {step === 2 && forgotConfig.step2Subtitle}
                {step === 3 && forgotConfig.step3Subtitle}
              </p>
            </div>
            {step === 1 && (
              <>
                {messageBox.show && (
                  <div >
                    <MessageBox
                      type={messageBox.type}
                      message={messageBox.text}
                      onClose={() => setMessageBox(prev => ({ ...prev, show: false }))}
                      showIcon={true}
                      showCloseButton={true}
                    />
                  </div>
                )}
                <FormInput type="email" name="forgotEmail" placeholder="Enter your email address" value={forgotEmail}
                  onChange={(e) => {
                    setForgotEmail(e.target.value);
                    setForgotErrors({});
                  }}
                  validationError={forgotErrors.email}
                />
                <div className="mt-4 text-center">
                  <button type="button" className="btn red-btn w-100" onClick={handleSendOtp} disabled={isLoading}>
                    {isLoading ? 'Sending...' : forgotConfig.buttonText}
                  </button>
                  <button type="button" className="border-0 bg-transparent text-light-grey text-14 mt-3" onClick={handleBackToLogin}>
                    Back to Login
                  </button>
                </div>
              </>
            )}
            {step === 2 && (
              <>
                {messageBox.show && (
                  <div >
                    <MessageBox
                      type={messageBox.type}
                      message={messageBox.text}
                      onClose={() => setMessageBox(prev => ({ ...prev, show: false }))}
                      showIcon={true}
                      showCloseButton={true}
                    />
                  </div>
                )}
                <p className="text-center mb-3 text-muted small">
                  OTP sent to <strong>{forgotEmail}</strong>
                </p>
                <div className="d-flex align-items-center gap-2 justify-content-center mb-4">
                  {otp.map((digit, index) => (
                    <input key={index} id={`fp-otp-${index}`} type="text" maxLength="1" className="form-control text-center" style={{ width: '50px', height: '50px', fontSize: '18px' }} value={digit} onChange={(e) => handleOtpChange(index, e.target.value)} />
                  ))}
                </div>
                {forgotErrors.otp && (
                  <div className="text-danger text-center mb-3 small">{forgotErrors.otp}</div>
                )}
                <div className="mt-2 text-center">
                  <button type="button" className="btn red-btn w-100" onClick={handleVerifyOtp} disabled={isLoading}>
                    {isLoading ? 'Verifying...' : forgotConfig.verifyText}
                  </button>
                  <div className="mt-3">
                    <button type="button" className="border-0 bg-transparent text-light-grey text-14" onClick={handleResendOtp} disabled={!canResend || isLoading} style={{ cursor: canResend ? 'pointer' : 'not-allowed' }}>
                      {isLoading ? 'Sending...' : canResend ? 'Resend OTP' : `Resend in ${timer}s`}
                    </button>
                  </div>
                  <button type="button" className="border-0 bg-transparent text-light-grey text-14 mt-2" onClick={() => setStep(1)}>
                    Back
                  </button>
                </div>
              </>
            )}
            {step === 3 && (
              <>
                <div className="mb-3">
                  <FormInput type="password" name="newPassword" placeholder="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} isPassword={true} validationError={forgotErrors.password} />
                </div>
                <div className="mb-3">
                  <FormInput type="password" name="confirmPassword" placeholder="Confirm New Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} isPassword={true} validationError={forgotErrors.confirmPassword} />
                </div>
                <div className="mt-4 text-center">
                  <button type="button" className="btn red-btn w-100" onClick={handleResetPassword} disabled={isLoading}>
                    {isLoading ? 'Resetting...' : forgotConfig.resetText}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AuthModal>
  );
};

export default Login;